/** Folder identity and ancestry. Labels never determine identity. */
export interface FolderOption {
  value: string
  label: string
  parentId?: string | null
}

export interface FolderNode extends FolderOption {
  children: FolderNode[]
  path: readonly FolderOption[]
}

/** Preserve sibling order. Exclude incomplete ancestry, cycles and duplicate IDs. */
export function buildFolderTree(options: readonly FolderOption[]) {
  const source = new Map<string, FolderOption>()
  const duplicateIds = new Set<string>()
  for (const option of options) {
    if (source.has(option.value)) duplicateIds.add(option.value)
    source.set(option.value, option)
  }
  const byId = new Map<string, FolderNode>()
  const rejected = new Set(duplicateIds)
  const visiting = new Set<string>()
  const resolve = (id: string): FolderNode | undefined => {
    if (byId.has(id)) return byId.get(id)
    const option = source.get(id)
    if (!option || rejected.has(id) || visiting.has(id)) return undefined
    visiting.add(id)
    const parent = option.parentId == null ? undefined : resolve(option.parentId)
    visiting.delete(id)
    if (option.parentId != null && parent === undefined) {
      rejected.add(id)
      return undefined
    }
    const node: FolderNode = { ...option, children: [], path: [...(parent?.path ?? []), option] }
    byId.set(id, node)
    return node
  }
  for (const option of options) resolve(option.value)
  const roots: FolderNode[] = []
  for (const option of options) {
    const node = byId.get(option.value)
    if (!node) continue
    if (option.parentId == null) roots.push(node)
    else byId.get(option.parentId)?.children.push(node)
  }
  return { roots, byId }
}

export const folderPathLabel = (node: FolderNode): string => node.path.map((part) => part.label).join(' / ')

/** Expand only for matching; keep the reader's selected IDs unchanged. */
export function folderSelectionIds(options: readonly FolderOption[], selected: readonly string[]): ReadonlySet<string> {
  const tree = buildFolderTree(options)
  const wanted = new Set(selected)
  return new Set([...tree.byId.values()].filter((node) => node.path.some((part) => wanted.has(part.value))).map((node) => node.value))
}
